import { useCallback, useMemo } from 'react';
import { IGetGroupInvitationsParams, OauthLoginProvider } from 'multirpc-sdk';

import { IUseQueryProps } from 'store/queries/types';
import { getQueryParams } from 'store/utils/getQueryParams';
import { useAppSelector } from 'store/useAppSelector';
import { useAuth } from 'modules/auth/hooks/useAuth';
import { useAssociatedAccounts } from 'modules/user/hooks/useAssociatedAccounts';

import {
  selectTeamInvitations,
  selectTeamInvitationsLoading,
  selectTeamInvitationsState,
  useFetchTeamInvitationsQuery,
  useLazyFetchTeamInvitationsQuery,
} from '../fetchTeamInvitations';

export interface IUseTeamInvitationsQueryProps
  extends IUseQueryProps,
    IGetGroupInvitationsParams {}

export const useTeamInvitationsQuery = ({
  invitationsStatuses,
  pollingInterval,
  skipFetching,
}: IUseTeamInvitationsQueryProps) => {
  const { isLoggedIn } = useAuth();

  const { associatedAccounts } = useAssociatedAccounts({
    skipFetching: !isLoggedIn,
  });

  const params = useMemo(
    (): IGetGroupInvitationsParams => ({ invitationsStatuses }),
    [invitationsStatuses],
  );

  const googleProvider = useMemo(() => {
    const foundProvider = associatedAccounts?.find(
      provider => provider.provider === OauthLoginProvider.Google,
    );

    return foundProvider;
  }, [associatedAccounts]);

  useFetchTeamInvitationsQuery(
    getQueryParams({
      skipFetching: !googleProvider || skipFetching,
      params: undefined,
    }),
    {
      pollingInterval,
    },
  );

  const [fetchLazy] = useLazyFetchTeamInvitationsQuery();

  const fetchTeamInvitations = useCallback(
    () => fetchLazy(params),
    [fetchLazy, params],
  );

  const teamInvitations = useAppSelector(state =>
    selectTeamInvitations(state, params),
  );
  const loading = useAppSelector(state =>
    selectTeamInvitationsLoading(state, params),
  );

  const state = useAppSelector(storeState =>
    selectTeamInvitationsState(storeState, params),
  );

  return {
    fetchTeamInvitations,
    loading,
    state,
    teamInvitations,
    teamInvitationsAmount: teamInvitations.length,
  };
};
